import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

// Import translations directly (not as default export to avoid circular dependency)
import * as translationsModule from './translations';

const translations = translationsModule.default || translationsModule;

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Get language from localStorage or default to French
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedLang = localStorage.getItem('religionova_language');
      return savedLang || 'fr';
    }
    return 'fr';
  });

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('religionova_language', language);
    // Set document direction for Arabic
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Memoize translation object to prevent unnecessary re-renders
  const t = useMemo(() => {
    return translations[language] || translations.fr;
  }, [language]);

  const changeLanguage = (newLang) => {
    if (translations[newLang]) {
      setLanguage(newLang);
    }
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const value = useMemo(() => ({
    language,
    changeLanguage,
    t,
    languages
  }), [language, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
